/**
 * Barra de filtros única de la dirección v2 (DESIGN.md §11.3, maqueta m1b-filtros): una línea de 60 px con el control
 * segmentado del tipo, el botón «Filtros (n)» que abre la capa, las fichas de los filtros activos con × y, a la
 * derecha, el conteo y el orden. El buscador vive en la cabecera (`BuscadorCabecera`).
 */
import { FormEvent, KeyboardEvent, ReactNode, RefObject, SelectHTMLAttributes, useCallback, useEffect, useId, useMemo, useRef, useState } from 'react';
import { ChevronDown, History, Search, SlidersHorizontal, Sparkles, X } from 'lucide-react';
import { FOCO } from './piezas';

/** Contenedor de la barra: 60 px, línea fina abajo. */
export function BarraFiltros({ children, derecha }: { children: ReactNode; derecha?: ReactNode }) {
  return (
    <div className="h-[60px] flex items-center gap-2.5 border-b border-slate-100" role="toolbar" aria-label="Filtros de la lista">
      <div className="flex items-center gap-2.5 min-w-0 flex-1">{children}</div>
      {derecha && <div className="flex items-center gap-3 shrink-0 text-[13px]">{derecha}</div>}
    </div>
  );
}

/** Control segmentado (tipo de documento, biblioteca). */
export function Segmentado<T extends string>({ opciones, valor, onChange, etiqueta }: {
  opciones: { valor: T; texto: string }[]; valor: T; onChange: (v: T) => void; etiqueta: string;
}) {
  return (
    <div className="flex gap-0.5 bg-slate-100 rounded-[9px] p-[3px] shrink-0" role="group" aria-label={etiqueta}>
      {opciones.map(o => (
        <button
          key={o.valor || 'todos'}
          type="button"
          aria-pressed={valor === o.valor}
          onClick={() => onChange(o.valor)}
          className={`text-[13px] px-3 py-[5px] rounded-[7px] whitespace-nowrap transition-colors motion-reduce:transition-none ${FOCO} ${
            valor === o.valor ? 'bg-white text-slate-900 font-medium shadow-[0_0_0_1px_rgba(15,23,42,.06),0_1px_2px_rgba(0,0,0,.04)]' : 'text-slate-600 hover:text-slate-900'
          }`}
        >
          {o.texto}
        </button>
      ))}
    </div>
  );
}

/** Ficha de un filtro activo, con × para quitarlo. */
export function FichaFiltro({ texto, onQuitar }: { texto: string; onQuitar: () => void }) {
  return (
    <span className="h-7 rounded-[7px] bg-slate-100 text-slate-700 text-[12.5px] pl-2.5 pr-1 flex items-center gap-1 max-w-[220px] shrink-0">
      <span className="truncate">{texto}</span>
      <button type="button" onClick={onQuitar} aria-label={`Quitar filtro: ${texto}`} className={`p-0.5 rounded text-slate-500 hover:text-slate-900 ${FOCO}`}>
        <X className="w-3 h-3" />
      </button>
    </span>
  );
}

/**
 * Botón «Filtros» con el número de filtros activos y la capa de 420 px debajo (popover). Escape y clic fuera la
 * cierran y devuelven el foco al botón (DESIGN.md §3).
 */
export function BotonFiltros({ activos, abierta, onAbrir, onCerrar, children }: {
  activos: number; abierta: boolean; onAbrir: () => void; onCerrar: () => void; children: ReactNode;
}) {
  const caja = useRef<HTMLDivElement>(null);
  const boton = useRef<HTMLButtonElement>(null);
  const idCapa = useId();
  const estabaAbierta = useRef(abierta);
  useEffect(() => {
    // Al cerrarse con «Listo» el foco quedaría en <body> (el botón pulsado desaparece): vuelve a «Filtros».
    if (estabaAbierta.current && !abierta && (document.activeElement === document.body || !document.activeElement)) boton.current?.focus();
    estabaAbierta.current = abierta;
  }, [abierta]);
  useEffect(() => {
    if (!abierta) return;
    const fuera = (e: MouseEvent) => {
      const t = e.target as Node;
      // Un elemento que ya salió del DOM (la sugerencia elegida, que se cierra en el mismo clic) no cuenta como «fuera».
      if (!t.isConnected) return;
      if (caja.current && !caja.current.contains(t)) onCerrar();
    };
    const tecla = (e: globalThis.KeyboardEvent) => {
      if (e.key !== 'Escape') return;
      // Un campo con sugerencias abiertas cierra primero su lista (Combo); el siguiente Escape cierra la capa.
      const t = e.target;
      if (t instanceof HTMLElement && t.getAttribute('role') === 'combobox' && t.getAttribute('aria-expanded') === 'true') return;
      e.stopPropagation(); // cierra solo la capa, no el panel lateral (que escucha Escape en window)
      onCerrar();
      boton.current?.focus();
    };
    document.addEventListener('mousedown', fuera);
    document.addEventListener('keydown', tecla, true);
    return () => { document.removeEventListener('mousedown', fuera); document.removeEventListener('keydown', tecla, true); };
  }, [abierta, onCerrar]);
  const on = abierta || activos > 0;
  return (
    <div ref={caja} className="relative shrink-0">
      <button
        ref={boton}
        type="button"
        onClick={() => (abierta ? onCerrar() : onAbrir())}
        aria-expanded={abierta}
        aria-haspopup="dialog"
        aria-controls={abierta ? idCapa : undefined}
        className={`h-8 rounded-lg border px-[11px] flex items-center gap-[7px] text-[13px] transition-colors motion-reduce:transition-none ${FOCO} ${
          on ? 'border-indigo-200 bg-indigo-50 text-indigo-700' : 'border-slate-200 text-slate-700 hover:bg-slate-50'
        }`}
      >
        <SlidersHorizontal className="w-3.5 h-3.5" aria-hidden="true" />
        Filtros
        {activos > 0 && (
          <span className="text-[11px] font-semibold bg-indigo-600 text-white rounded-full min-w-[17px] h-[17px] px-[5px] grid place-items-center tabular-nums">
            {activos}<span className="sr-only"> activos</span>
          </span>
        )}
      </button>
      {abierta && (
        <div
          id={idCapa}
          role="dialog"
          aria-label="Filtros"
          className="capa-elevada absolute left-0 top-full mt-2 w-[420px] bg-white rounded-[10px] py-1.5 z-30 border border-slate-200 shadow-[0_12px_32px_rgba(15,23,42,.12)]"
        >
          {children}
        </div>
      )}
    </div>
  );
}

/** Sección de la capa con su rótulo. */
export function SeccionCapa({ titulo, children }: { titulo: string; children: ReactNode }) {
  return (
    <div className="px-[18px] py-3 border-b border-slate-100 last-of-type:border-b-0" role="group" aria-label={titulo}>
      <div className="text-[12px] font-medium text-slate-500 mb-2">{titulo}</div>
      {children}
    </div>
  );
}

/** Opción de la capa (materia): chip con borde; activa en indigo. */
export function OpcionCapa({ activa, onClick, children }: { activa: boolean; onClick: () => void; children: ReactNode }) {
  return (
    <button
      type="button"
      aria-pressed={activa}
      onClick={onClick}
      className={`text-[13px] px-2.5 py-[5px] rounded-[7px] border transition-colors motion-reduce:transition-none ${FOCO} ${
        activa ? 'border-indigo-200 bg-indigo-50 text-indigo-700 font-medium' : 'border-slate-200 text-slate-700 hover:bg-slate-50'
      }`}
    >
      {children}
    </button>
  );
}

/** Clase de los campos de la capa (selectores y texto), 34 px. */
export const CAMPO_CAPA = `h-[34px] w-full border border-slate-200 rounded-lg px-2.5 text-[13px] text-slate-700 bg-white focus:outline-none focus:border-indigo-200 disabled:bg-slate-50 disabled:text-slate-400 ${FOCO}`;

/**
 * Selector de la capa: flecha fina (lucide) en vez de la del sistema y el marcador en gris mientras no haya valor
 * (maqueta m1b).
 */
export function SelectCapa({ value, children, className = '', ...resto }: SelectHTMLAttributes<HTMLSelectElement>) {
  return (
    <div className="relative">
      <select value={value} {...resto} className={`${CAMPO_CAPA} appearance-none pr-8 ${value ? '' : 'text-slate-400'} ${className}`}>
        {children}
      </select>
      <ChevronDown className="w-4 h-4 text-slate-400 absolute right-2.5 top-1/2 -translate-y-1/2 pointer-events-none" aria-hidden="true" />
    </div>
  );
}

/** Pie de la capa: «Limpiar» a la izquierda y «Listo» a la derecha. */
export function PieCapa({ onLimpiar, onListo }: { onLimpiar: () => void; onListo: () => void }) {
  return (
    <div className="flex items-center justify-between px-[18px] pt-2.5 pb-2 text-[13px]">
      <button type="button" onClick={onLimpiar} className={`text-slate-500 hover:text-slate-800 rounded ${FOCO}`}>Limpiar</button>
      <button type="button" onClick={onListo} className={`text-indigo-700 font-medium hover:text-indigo-900 rounded ${FOCO}`}>Listo</button>
    </div>
  );
}

export interface Reciente { id: string; texto: string; enChat: boolean }

/**
 * Buscador de la cabecera (§11.1): «/» lo enfoca; con texto ofrece «Preguntar en el chat»; vacío y enfocado muestra
 * las búsquedas recientes en un desplegable (antes iban en una fila de chips bajo el buscador).
 */
export function BuscadorCabecera({ valor, onChange, onBuscar, onBorrar, onPreguntar, placeholder, etiqueta, recientes, onReciente, entrada }: {
  valor: string;
  onChange: (v: string) => void;
  onBuscar: (e?: FormEvent) => void;
  onBorrar: () => void;
  onPreguntar?: () => void;
  placeholder: string;
  etiqueta: string;
  recientes?: Reciente[];
  onReciente?: (id: string) => void;
  entrada: RefObject<HTMLInputElement>;
}) {
  const [foco, setFoco] = useState(false);
  const verRecientes = foco && !valor.trim() && !!recientes?.length;
  return (
    <form
      onSubmit={onBuscar}
      role="search"
      className="relative w-full"
      // La lista sigue abierta mientras el foco esté dentro del buscador (también al pasar con Tab a una reciente).
      onFocus={() => setFoco(true)}
      onBlur={e => { if (!e.currentTarget.contains(e.relatedTarget as Node | null)) setFoco(false); }}
      onKeyDown={e => { if (e.key === 'Escape' && verRecientes) { e.stopPropagation(); setFoco(false); entrada.current?.focus(); } }}
    >
      <div className="h-10 flex items-center gap-2 rounded-xl border border-slate-200 bg-white px-3 shadow-[0_1px_2px_rgba(0,0,0,.04)] focus-within:border-indigo-200">
        <Search className="w-4 h-4 text-slate-400 shrink-0" aria-hidden="true" />
        <input
          ref={entrada}
          type="text"
          value={valor}
          onChange={e => onChange(e.target.value)}
          placeholder={placeholder}
          aria-label={etiqueta}
          className="flex-1 min-w-0 text-[13.5px] text-slate-900 placeholder:text-slate-400 focus:outline-none bg-transparent"
        />
        {valor && (
          <button type="button" onClick={onBorrar} aria-label="Borrar búsqueda" className={`text-slate-400 hover:text-slate-600 rounded ${FOCO}`}>
            <X className="w-3.5 h-3.5" />
          </button>
        )}
        {valor.trim() && onPreguntar && (
          <button type="button" onClick={onPreguntar} title="Hacer esta pregunta en el chat" className={`px-2 py-1 rounded-md text-[11.5px] font-medium text-slate-500 hover:text-slate-900 hover:bg-slate-100 flex items-center gap-1 whitespace-nowrap ${FOCO}`}>
            <Sparkles className="w-3 h-3" aria-hidden="true" /> Preguntar en el chat
          </button>
        )}
        <kbd className="px-1.5 py-0.5 text-[11px] text-slate-400 bg-slate-50 border border-slate-200 rounded" title="Atajo: / lleva al buscador">/</kbd>
      </div>
      {verRecientes && (
        <div className="capa-elevada absolute left-0 right-0 top-full mt-1.5 bg-white rounded-[10px] py-1.5 z-30 border border-slate-200 shadow-[0_12px_32px_rgba(15,23,42,.12)]" aria-label="Búsquedas recientes" role="group">
          <div className="px-3 pt-0.5 pb-1 text-[12px] text-slate-500">Búsquedas recientes</div>
          {recientes!.map(r => (
            <button
              key={r.id}
              type="button"
              // Safari no enfoca un botón al hacer clic: sin esto el campo pierde el foco, la lista se cierra y el clic no llega.
              onMouseDown={e => e.preventDefault()}
              onClick={() => { setFoco(false); onReciente?.(r.id); }}
              className={`w-full px-3 py-1.5 text-left text-[13px] text-slate-700 hover:bg-slate-50 flex items-center gap-2 ${FOCO}`}
              title={r.enChat ? `${r.texto} (en el chat)` : r.texto}
            >
              {r.enChat ? <Sparkles className="w-3.5 h-3.5 text-slate-400 shrink-0" aria-hidden="true" /> : <History className="w-3.5 h-3.5 text-slate-400 shrink-0" aria-hidden="true" />}
              <span className="truncate">{r.texto}</span>
            </button>
          ))}
        </div>
      )}
    </form>
  );
}

export interface Norma { ley_codigo: string; convenio_pais: string; otro_codigo: string; articulos: string[] }

/**
 * «art. 31 nº 12, 21» → ['31 N° 12', '21']: el formato que entiende el filtro del backend, que solo reconoce «N°» con
 * mayúscula y signo de grado (`applyLegislationFilters`).
 */
export const articulosDeTexto = (t: string) =>
  t.split(/[,;]/)
    .map(a => a.trim().replace(/^(?:art[ií]culos?|arts?)\.?\s*/i, '').replace(/\bn(?:[°º.o]|ro\.?|úm\.?|um\.?)?\s*(\d+)/gi, 'N° $1').trim())
    .filter(Boolean);

/** Artículo del catálogo de legislación (`/legislation/catalog`), con sus numerales y letras. */
export interface ArticuloCatalogo {
  num: string;
  titulo: string;
  subnumeros?: { num?: string; letra?: string; titulo: string }[];
  letras?: { letra: string; titulo: string }[];
}

export interface CatalogoNormas {
  estado: 'cargando' | 'error' | 'ok';
  leyes: { codigo: string; nombre: string; articulos?: ArticuloCatalogo[] }[];
  convenios: string[];
  otros: { codigo: string; nombre: string }[];
}

interface OpcionCombo { valor: string; texto: string; detalle?: string; grupo?: string }

/**
 * Árbol de artículos de una ley, aplanado como opciones, con los mismos ids que usaba `FilterDrawer` y que entiende el
 * backend: «31», «31 N° 12», «14 A».
 */
export function opcionesArticulos(arts: ArticuloCatalogo[] = []): OpcionCombo[] {
  const out: OpcionCombo[] = [];
  for (const a of arts) {
    out.push({ valor: a.num, texto: `Art. ${a.num}`, detalle: a.titulo });
    for (const sub of a.subnumeros || []) {
      const id = sub.num ? `${a.num} N° ${sub.num}` : `${a.num} ${sub.letra}`;
      out.push({ valor: id, texto: `Art. ${id}`, detalle: sub.titulo });
    }
    for (const l of a.letras || []) out.push({ valor: `${a.num} ${l.letra}`, texto: `Art. ${a.num} letra ${l.letra})`, detalle: l.titulo });
  }
  return out;
}

const plegarTexto = (t: string) => t.normalize('NFD').replace(/[̀-ͯ]/g, '').toLowerCase();

/**
 * Campo con sugerencias al escribir (patrón combobox de WAI-ARIA): ↑ ↓ recorren, Enter elige, Escape cierra la lista
 * sin cerrar la capa. Con `onLibre`, Enter o salir del campo aceptan lo escrito aunque no calce con una sugerencia.
 */
function Combo({ etiqueta, placeholder, opciones, mostrar, onElegir, onLibre, deshabilitado = false, limite = 40, listaAncha = false }: {
  etiqueta: string;
  placeholder: string;
  opciones: OpcionCombo[];
  /** Texto del campo cuando no se está escribiendo (lo elegido). */
  mostrar?: string;
  onElegir: (valor: string) => void;
  onLibre?: (texto: string) => void;
  deshabilitado?: boolean;
  limite?: number;
  /** La lista ocupa el ancho de la capa (384 px) alineada a la derecha, para que se lean los títulos. */
  listaAncha?: boolean;
}) {
  const id = useId();
  const [texto, setTexto] = useState('');
  const [abierto, setAbierto] = useState(false);
  const [activo, setActivo] = useState(0);
  const pendiente = useRef({ texto, onLibre });
  pendiente.current = { texto, onLibre };
  // Al desmontarse (capa cerrada) lo escrito no se pierde.
  useEffect(() => () => { const p = pendiente.current; if (p.onLibre && p.texto.trim()) p.onLibre(p.texto); }, []);

  const tokens = plegarTexto(texto).split(/\s+/).filter(Boolean);
  const filtradas = (tokens.length ? opciones.filter(o => { const h = plegarTexto(`${o.texto} ${o.detalle || ''}`); return tokens.every(t => h.includes(t)); }) : opciones).slice(0, limite);
  const elegir = (o: OpcionCombo) => { setTexto(''); setAbierto(false); onElegir(o.valor); };
  const libre = () => { if (onLibre && texto.trim()) { onLibre(texto); setTexto(''); } };
  const tecla = (e: KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'ArrowDown') { e.preventDefault(); setAbierto(true); setActivo(a => Math.min(a + 1, filtradas.length - 1)); }
    else if (e.key === 'ArrowUp') { e.preventDefault(); setActivo(a => Math.max(a - 1, 0)); }
    else if (e.key === 'Enter') {
      e.preventDefault();
      if (abierto && filtradas[activo] && (texto.trim() || !onLibre)) elegir(filtradas[activo]);
      else libre();
    } else if (e.key === 'Escape' && abierto) { e.stopPropagation(); e.nativeEvent.stopImmediatePropagation(); setAbierto(false); }
  };
  let grupo: string | undefined;
  return (
    <div className="relative">
      <input
        type="text"
        role="combobox"
        aria-label={etiqueta}
        aria-expanded={abierto}
        aria-controls={`${id}-lista`}
        aria-autocomplete="list"
        aria-activedescendant={abierto && filtradas[activo] ? `${id}-${activo}` : undefined}
        value={abierto || texto ? texto : mostrar || ''}
        onChange={e => { setTexto(e.target.value); setAbierto(true); setActivo(0); }}
        onFocus={() => { setAbierto(true); setActivo(0); }}
        onBlur={() => { setAbierto(false); libre(); }}
        onKeyDown={tecla}
        disabled={deshabilitado}
        placeholder={placeholder}
        className={`${CAMPO_CAPA} pr-8 ${!abierto && !texto && mostrar ? 'text-slate-700' : ''}`}
      />
      <ChevronDown className="w-4 h-4 text-slate-400 absolute right-2.5 top-1/2 -translate-y-1/2 pointer-events-none" aria-hidden="true" />
      {abierto && filtradas.length > 0 && (
        <ul id={`${id}-lista`} role="listbox" aria-label={etiqueta} className={`capa-elevada absolute top-full ${listaAncha ? 'right-0 w-[384px]' : 'left-0 right-0'} mt-1 max-h-56 overflow-y-auto bg-white rounded-[10px] border border-slate-200 py-1 z-40 shadow-[0_12px_32px_rgba(15,23,42,.12)]`}>
          {filtradas.map((o, i) => {
            const cabecera = o.grupo && o.grupo !== grupo ? o.grupo : null;
            grupo = o.grupo;
            return (
              <li key={o.valor} role="presentation">
                {cabecera && <div className="px-3 pt-2 pb-1 text-[11.5px] font-medium text-slate-500" role="presentation">{cabecera}</div>}
                <div
                  id={`${id}-${i}`}
                  role="option"
                  aria-selected={i === activo}
                  // mousedown: el clic llega antes de que el campo pierda el foco
                  onMouseDown={e => { e.preventDefault(); elegir(o); }}
                  onMouseEnter={() => setActivo(i)}
                  className={`px-3 py-1.5 text-[13px] cursor-pointer flex gap-2 ${i === activo ? 'bg-slate-50 text-slate-900' : 'text-slate-700'}`}
                >
                  <span className="shrink-0 tabular-nums">{o.texto}</span>
                  {o.detalle && <span className="text-slate-500 truncate">— {o.detalle}</span>}
                </div>
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}

/**
 * «Norma citada» de la capa (reemplaza al panel «Ley y artículo», `FilterDrawer`, sin perder sus funciones; pedido del
 * dueño 19-10): un buscador de leyes, convenios (por país) y otras normas, y el árbol de artículos de la ley elegida
 * como sugerencias con su título. Cada artículo elegido es una ficha con ×; también se acepta un artículo escrito a
 * mano («31 nº 12»). Efecto inmediato.
 */
export function NormaCitada({ valor, onChange, catalogo }: { valor: Norma; onChange: (n: Norma) => void; catalogo: CatalogoNormas }) {
  const ley = catalogo.leyes.find(l => l.codigo === valor.ley_codigo);
  const normas: OpcionCombo[] = [
    ...catalogo.leyes.map(l => ({ valor: `ley:${l.codigo}`, texto: l.nombre, grupo: 'Leyes' })),
    ...catalogo.convenios.map(p => ({ valor: `conv:${p}`, texto: p, detalle: 'convenio', grupo: 'Convenios' })),
    ...catalogo.otros.map(o => ({ valor: `otro:${o.codigo}`, texto: o.nombre, grupo: 'Otras normas' })),
  ];
  const elegida = ley ? ley.nombre : valor.convenio_pais ? `Convenio con ${valor.convenio_pais}` : catalogo.otros.find(o => o.codigo === valor.otro_codigo)?.nombre || valor.otro_codigo;
  const articulos = useMemo(() => opcionesArticulos(ley?.articulos), [ley]);
  const elegirNorma = (v: string) => {
    const [clase, ...resto] = v.split(':');
    const idNorma = resto.join(':');
    onChange({ ley_codigo: clase === 'ley' ? idNorma : '', convenio_pais: clase === 'conv' ? idNorma : '', otro_codigo: clase === 'otro' ? idNorma : '', articulos: [] });
  };
  // `valor` cambia con cada filtro: se lee del ref para que agregar o quitar use siempre el último.
  const actual = useRef(valor);
  actual.current = valor;
  const agregar = useCallback((nuevos: string[]) => {
    const v = actual.current;
    if (!v.ley_codigo || !nuevos.length) return;
    const lista = Array.from(new Set([...v.articulos, ...nuevos]));
    if (lista.length !== v.articulos.length) onChange({ ...v, articulos: lista });
  }, [onChange]);
  const quitar = (a: string) => onChange({ ...valor, articulos: valor.articulos.filter(x => x !== a) });
  const sinCatalogo = catalogo.estado !== 'ok';
  return (
    <div className="space-y-2">
      <div className="grid grid-cols-2 gap-2">
        <Combo
          etiqueta="Norma citada"
          placeholder={catalogo.estado === 'cargando' ? 'Cargando normas…' : catalogo.estado === 'error' ? 'No se pudieron cargar' : 'Buscar ley o convenio'}
          opciones={normas}
          mostrar={elegida}
          onElegir={elegirNorma}
          deshabilitado={sinCatalogo}
          limite={80}
        />
        <Combo
          etiqueta="Artículo"
          placeholder={valor.ley_codigo ? 'Artículo, ej. 31 N° 12' : 'Elige primero una ley'}
          opciones={articulos}
          onElegir={a => agregar([a])}
          onLibre={t => agregar(articulosDeTexto(t))}
          deshabilitado={!valor.ley_codigo}
          listaAncha
        />
      </div>
      {valor.articulos.length > 0 && (
        <div className="flex flex-wrap gap-1.5" aria-label="Artículos elegidos">
          {valor.articulos.map(a => <FichaFiltro key={a} texto={`Art. ${a}`} onQuitar={() => quitar(a)} />)}
        </div>
      )}
    </div>
  );
}
